import {useCallback, useMemo} from 'react';

import {TraceServerClient} from '../../traceServerClient';
import {useGetTraceServerClientContext} from '../../traceServerClientContext';
import {trackLLMGeneration} from '../analytics';
import {DEFAULT_TRACK_CALL, DEFAULT_UPDATE_INTERVAL} from '../constants';
import {useMagicContext} from '../context';
import {
  combineChunks,
  createChunkProcessor,
  isResponseFormat,
} from '../streaming';
import {ChatCompletionParams, Chunk, Completion, EntityProject} from '../types';
import {prepareMessages, prepareResponseFormat} from './prepare';

/**
 * Error thrown during streaming operations.
 */
class StreamingError extends Error {
  public readonly cause?: Error;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'StreamingError';
    this.cause = cause instanceof Error ? cause : undefined;
  }
}

/**
 * Error thrown during response parsing operations.
 */
class ResponseParsingError extends Error {
  public readonly cause?: Error;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ResponseParsingError';
    this.cause = cause instanceof Error ? cause : undefined;
  }
}

/**
 * Make a streaming chat completion request to the trace server.
 *
 * @param client The trace server client instance
 * @param entity The entity (organization) name
 * @param project The project name
 * @param params Chat completion parameters
 * @param onChunk Callback for each streaming chunk
 * @param updateInterval Throttle interval for updates (default: 150ms)
 * @param signal Optional AbortSignal for cancellation
 * @returns The complete response as a string
 */
export const chatCompleteStream = async (
  client: TraceServerClient,
  entity: string,
  project: string,
  params: ChatCompletionParams,
  onChunk: (chunk: Chunk) => void,
  updateInterval: number = 150,
  signal?: AbortSignal
): Promise<Completion> => {
  // Track analytics (required)
  trackLLMGeneration({
    entity,
    project,
    task: params.analyticsConfig.task,
    area: params.analyticsConfig.area,
    ...(params.analyticsConfig.metadata && {
      metadata: params.analyticsConfig.metadata,
    }),
  });

  // Create chunk processor
  const processor = createChunkProcessor(
    onChunk,
    params.responseFormat,
    updateInterval,
    signal
  );

  try {
    const args = {
      project_id: `${entity}/${project}`,
      inputs: {
        model: params.weavePlaygroundModelId,
        messages: prepareMessages(params.messages),
        temperature: params.temperature,
        response_format: prepareResponseFormat(params.responseFormat),
        tools: params.tools,
      },
    };

    await client.completionsCreateStream(
      {
        ...args,
        track_llm_call: params.trackCall ?? DEFAULT_TRACK_CALL,
      },
      processor.processChunk
    );

    // Process any remaining chunks
    processor.cleanup();

    const rawRes = combineChunks(processor.getProcessedChunks());
    let res: Completion;

    if (params.responseFormat) {
      if (
        isResponseFormat(params.responseFormat) &&
        params.responseFormat.type === 'text'
      ) {
        res = rawRes;
      } else {
        try {
          res = JSON.parse(rawRes);
          if ('parse' in params.responseFormat && params.responseFormat.parse) {
            res = params.responseFormat.parse(res);
          }
        } catch (error) {
          throw new ResponseParsingError(
            'Failed to parse response format',
            error
          );
        }
      }
    } else {
      res = rawRes;
    }

    return res;
  } catch (error) {
    if (error instanceof ResponseParsingError) {
      throw error;
    }
    throw new StreamingError('Chat completion failed', error);
  }
};

/**
 * Hook for making streaming chat completion requests.
 *
 * Returns a function that can be used to make streaming chat completion requests.
 * Uses the selected model from context unless overridden in params.
 *
 * When responseFormat is a Zod array schema, automatically handles structured streaming
 * with partial array updates as items stream in.
 *
 * @param entityProject Optional entity and project to use (defaults to context)
 * @param updateInterval Minimum milliseconds between UI updates (default: 150ms)
 */
export const useChatCompletionStream = (
  entityProject?: EntityProject,
  updateInterval: number = DEFAULT_UPDATE_INTERVAL
) => {
  const {entity, project, selectedModel} = useMagicContext();
  const {entity: finalEntity, project: finalProject} = useMemo(() => {
    if (entityProject) {
      return entityProject;
    }
    return {
      entity,
      project,
    };
  }, [entityProject, entity, project]);
  const getClient = useGetTraceServerClientContext();

  return useCallback(
    (
      params: Omit<ChatCompletionParams, 'weavePlaygroundModelId'> & {
        weavePlaygroundModelId?: string;
      },
      onChunk: (chunk: Chunk) => void,
      signal?: AbortSignal
    ) => {
      const client = getClient();
      // Use selected model from context if not specified in params
      const weavePlaygroundModelId =
        params.weavePlaygroundModelId || selectedModel;

      return chatCompleteStream(
        client,
        finalEntity,
        finalProject,
        {...params, weavePlaygroundModelId: weavePlaygroundModelId},
        onChunk,
        updateInterval,
        signal
      );
    },
    [finalEntity, finalProject, getClient, selectedModel, updateInterval]
  );
};
